import { useAuthForgotPassword } from '@/api/gen-new/auth/auth';
import AuthFormLayout from '@/auth/components/AuthFormLayout';
import { routeAfterAuthenticated } from '@/auth/constants/authSettings';
import useAuth from '@/auth/useAuth';
import defaultNotifications from '@/components/functions/defaultNotifications';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextInput } from '@mantine/core';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import type { ResetPasswordPageState } from './ResetPasswordPage';

const schema = yup.object({
	email: yup.string().email('errors.emailInvalid').required('errors.emailRequired'),
});

type FormData = yup.InferType<typeof schema>;

const ForgotPasswordPage = () => {
	const navigate = useNavigate();
	const { isAuthenticated } = useAuth();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<FormData>({
		resolver: yupResolver(schema),
	});

	const { mutate: forgotPassword } = useAuthForgotPassword();

	useEffect(() => {
		if (isAuthenticated()) {
			navigate(routeAfterAuthenticated);
		}
	}, [isAuthenticated, navigate]);

	const onSubmit = async ({ email }: FormData) => {
		await forgotPassword(
			{
				data: {
					email,
				},
			},
			{
				onSuccess: () => {
					defaultNotifications.showSuccess({
						message: t('forgotPassword.emailSent'),
					});

					navigate('/reset-password', {
						replace: true,
						state: {
							email,
						} satisfies ResetPasswordPageState,
					});
				},
				onError: error => {
					let errorMessage = 'errors.unknownError';

					if (isAxiosError(error) && error.response) {
						if (error.response.status === 400) {
							errorMessage = 'errors.emailNotFound';
						} else {
							errorMessage = 'errors.unknownError';
						}
					}

					defaultNotifications.showError({
						message: t(`forgotPassword.${errorMessage}`),
					});
				},
			},
		);
	};

	return (
		<AuthFormLayout
			title={t('forgotPassword.title')}
			onSubmit={handleSubmit(onSubmit)}
			fields={
				<TextInput
					label={t('forgotPassword.email')}
					{...register('email')}
					error={errors.email && t(`forgotPassword.${errors.email.message}`)}
					autoFocus
				/>
			}
			actionButton={
				<Button type="submit" aria-disabled={isSubmitting} disabled={isSubmitting} variant="filled">
					{t('forgotPassword.forgotPassword')}
				</Button>
			}
			altAction={{
				preText: t('resetPassword.loginText'),
				text: t('resetPassword.login'),
				to: '/login',
			}}
		/>
	);
};

export default ForgotPasswordPage;
